import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Logo from 'ui-component/Logo';
import logo from 'assets/images/surveyFlip-logo-icon.svg';

const BasicSuccessComponent = () => {
    const [showConfetti, setShowConfetti] = useState(false);

    useEffect(() => {
        // Trigger confetti animation on component mount
        setShowConfetti(true);

        // Hide confetti after animation completes
        const timer = setTimeout(() => {
            setShowConfetti(false);
        }, 3000); // 3 seconds total duration

        return () => clearTimeout(timer);
    }, []);

    const getRandomColor = () => {
        const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3'];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const getRandomShape = () => {
        const shapes = [
            { borderRadius: '0%' }, // Square
            { borderRadius: '50%' }, // Circle
            { borderRadius: '0% 50% 50% 50%' }, // Teardrop
            {
                width: '15px',
                height: '8px',
                borderRadius: '50%'
            }, // Oval
            {
                width: '0',
                height: '0',
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderBottom: '10px solid',
                backgroundColor: 'transparent'
            }, // Triangle
            {
                width: '12px',
                height: '4px',
                borderRadius: '2px'
            }, // Rectangle
            {
                borderRadius: '50% 0% 50% 0%'
            }, // Leaf shape
            {
                width: '8px',
                height: '8px',
                transform: 'rotate(45deg)',
                borderRadius: '0%'
            } // Diamond
        ];
        return shapes[Math.floor(Math.random() * shapes.length)];
    };

    // Style objects
    const styles = {
        container: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
        },
        confetti: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1,
            animation: 'fadeIn 0.5s ease-in'
        },
        confettiPiece: {
            position: 'absolute',
            width: '10px',
            height: '10px',
            top: '-10px',
            animation: 'confetti-fall 3s linear infinite'
        },
        successContent: {
            textAlign: 'center',
            zIndex: 2,
            position: 'relative',
            maxWidth: '600px',
            padding: '40px 20px'
        },
        successTitle: {
            fontSize: '2rem',
            fontWeight: '700',
            color: '#2d3748',
            marginBottom: '20px',
            lineHeight: '1.2'
        },
        successSubtitle: {
            fontSize: '1.1rem',
            color: '#4a5568',
            marginBottom: '10px',
            fontWeight: '400'
        },
        roadmapText: {
            fontSize: '1rem',
            color: '#4a5568',
            marginBottom: '40px'
        },
        roadmapLink: {
            color: '#3182ce',
            textDecoration: 'underline',
            fontWeight: '500'
        },
        socialIcons: {
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            marginTop: '20px'
        },
        socialIcon: {
            width: '50px',
            height: '50px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            color: '#6b7280'
        },
        facebook: {
            // backgroundColor removed
        },
        twitter: {
            // backgroundColor removed
        },
        linkedin: {
            // backgroundColor removed
        },
        ctaButton: {
            background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
            color: 'white',
            border: 'none',
            padding: '16px 32px',
            fontSize: '1.1rem',
            fontWeight: '600',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: '0 4px 15px rgba(229, 62, 62, 0.3)',
            marginTop: '20px'
        }
    };

    // CSS keyframes as a style tag
    const keyframesCSS = `
    @keyframes fadeIn {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
    
    @keyframes confetti-burst {
      0% {
        transform: translate(0, 0) rotate(0deg) scale(0);
        opacity: 0;
      }
      5% {
        opacity: 1;
      }
      10% {
        transform: translate(var(--dx), var(--dy)) rotate(180deg) scale(1);
        opacity: 1;
      }
      100% {
        transform: translate(calc(var(--dx) * 2), calc(var(--dy) * 2)) rotate(720deg) scale(0.3);
        opacity: 0;
      }
    }
    
    .social-icon:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
    
    .cta-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(229, 62, 62, 0.4);
    }
    
    .cta-button:active {
      transform: translateY(0);
    }
    
    .roadmap-link:hover {
      color: #2c5282;
    }
    
    @media (max-width: 768px) {
      .success-title {
        font-size: 2rem !important;
      }
      
      .success-content {
        padding: 20px 15px !important;
      }
      
      .cta-button {
        padding: 14px 24px !important;
        font-size: 1rem !important;
      }
    }
  `;

    return (
        <>
            <style>{keyframesCSS}</style>
            <div style={styles.container}>
                {/* Confetti Animation - Only show on first load */}
                {showConfetti && (
                    <div style={styles.confetti}>
                        {[...Array(40)].map((_, i) => {
                            const angle = (i / 40) * 360;
                            const velocity = 80 + Math.random() * 120;
                            const dx = Math.cos((angle * Math.PI) / 180) * velocity;
                            const dy = Math.sin((angle * Math.PI) / 180) * velocity;
                            const shape = getRandomShape();
                            const color = getRandomColor();

                            return (
                                <div
                                    key={i}
                                    style={{
                                        ...styles.confettiPiece,
                                        ...shape,
                                        left: '50%',
                                        top: '50%',
                                        '--dx': `${dx}px`,
                                        '--dy': `${dy}px`,
                                        animationDelay: `${Math.random() * 0.3}s`,
                                        backgroundColor: shape.backgroundColor === 'transparent' ? 'transparent' : color,
                                        borderBottomColor: shape.backgroundColor === 'transparent' ? color : undefined,
                                        animation: 'confetti-burst 2.5s ease-out forwards'
                                    }}
                                />
                            );
                        })}
                    </div>
                )}

                {/* Main Content */}
                <div style={styles.successContent} className="success-content">
                    <Link to="/">
                        <img src={logo} alt="Survey Flip" width="360" style={{ marginBottom: '2rem' }} />
                    </Link>
                    <h1 style={styles.successTitle} className="success-title">
                        Thank you for your response!
                    </h1>

                    <p style={styles.successSubtitle}>We&apos;re constantly adding new features to Surveyflip.</p>

                    {/* <p style={styles.roadmapText}>
                        Check out{' '}
                        <a href="#" style={styles.roadmapLink} className="roadmap-link">
                            Surveyflip&apos;s roadmap here
                        </a>
                    </p> */}

                    {/* CTA Button */}
                    <button
                        type="button"
                        onClick={() => window.open('https://client.surveyflip.com', '_blank')}
                        style={styles.ctaButton}
                        className="cta-button"
                    >
                        I am ready to create my own Survey on Surveyflip
                    </button>
                    {/* Social Media Icons */}
                    <div style={styles.socialIcons}>
                        <a href="#" style={{ ...styles.socialIcon, ...styles.facebook }} className="social-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                        </a>

                        <a href="#" style={{ ...styles.socialIcon, ...styles.twitter }} className="social-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                        </a>

                        <a href="#" style={{ ...styles.socialIcon, ...styles.linkedin }} className="social-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                        </a>
                    </div>

                    {/* Copyright */}
                    <div style={{ marginTop: '40px', fontSize: '0.9rem', color: '#6b7280' }}>&copy; SurveyFlip. All rights reserved.</div>
                </div>
            </div>
        </>
    );
};

export default BasicSuccessComponent;
